import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Collapse, message, Tooltip } from 'antd';
import AppLayout from '../../components/layout/AppLayout';
import { useTranslation } from 'react-i18next';
import { TChapter, TCourse } from '../../models/courses/course';
import { useCourseApi } from '../../api/courses/courses';
import { useUserApi } from '../../api/user/userApp';
import { TLessonProgress } from '../../models/user/UserProgression';
import { getAllLessonsFromCourse } from '../../models/courses/courseFunc';
import CourseSummaryCard from './CourseSummaryCard';
import { useTilmiAuth } from '../../hooks/useTilmiAuth';
import { CODIFICATION_TYPE_USERS } from '../../constant/codification';
import { readLastPosition } from '../../utils/progress/lastPosition';
import './CourseDetails.css';
import { usePaiementApi } from '../../api/paiement/paiement';

const CourseDetails: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { courseId } = useParams();
    const { getCoursesDetailsById } = useCourseApi();
    const [course, setCourse] = useState<TCourse | null>(null);
    const { getProgressionUserByCourseId } = useUserApi();
    const [progression, setProgression] = useState<TLessonProgress[]>([]);
    const [loading, setIsLoading] = useState<boolean>(false);
    const [progressPercent, setProgressPercent] = useState<number>(0);
    const [buttonText, setButtonText] = useState<string>(t('courses.details.enter', 'Enter the course'));
    const [nextLessonId, setNextLessonId] = useState<number>(0);
    // Chapitre ouvert dans l'accordeon : celui ou l'eleve en est, pas le premier.
    const [activeChapterKey, setActiveChapterKey] = useState<string | undefined>(undefined);
    const [searchParams] = useSearchParams();
    const hasScrolledToResume = useRef<boolean>(false);
    // Lecon ciblee explicitement par la page de progression apres la validation
    // d'une lecon. Elle fait autorite sur la position deduite de la progression,
    // qui peut etre en retard d'un enregistrement serveur.
    const targetLessonIdFromUrl = Number(searchParams.get('lesson')) || 0;
    const targetChapterIdFromUrl = Number(searchParams.get('chapter')) || 0;
    const { getCodeTypeUser, getTimeRemaining, getUserAppId } = useTilmiAuth();
    const paiementApi = usePaiementApi();
    const codeType = getCodeTypeUser();
    const isTeacher = codeType === CODIFICATION_TYPE_USERS.TEACHER;
    const isAdmin = codeType === CODIFICATION_TYPE_USERS.ADMIN;
    const isCourseOp = codeType === CODIFICATION_TYPE_USERS.COURSE_OP;
    const isPrivileged = isTeacher || isAdmin || isCourseOp;
    const [isPaid, setIsPaid] = useState<boolean>((getTimeRemaining() ?? -1) > 0);

    useEffect(() => {
        setIsLoading(true);
        Promise.all([
            getProgressionUserByCourseId(Number(courseId)),
            getCoursesDetailsById(Number(courseId)),
        ])
            .then((response) => {
                const [respProgression, respCourse] = response;

                const courseData: TCourse = respCourse.data;
                const progressionData: TLessonProgress[] = respProgression.data || [];

                setProgression(progressionData);
                setCourse(courseData);

                const allLessons = getAllLessonsFromCourse(courseData);

                // Position reelle de l'eleve : la premiere lecon non terminee ;
                // si tout est termine, la derniere lecon reellement ouverte.
                const upcomingLesson = allLessons.find(
                    (lesson) =>
                        lesson &&
                        progressionData &&
                        !progressionData.some((prog) => prog.lesson_id === lesson.id)
                );
                const storedLessonId = readLastPosition(getUserAppId(), Number(courseId));
                const storedLesson = storedLessonId
                    ? allLessons.find((lesson) => lesson?.id === storedLessonId)
                    : undefined;
                // La cible passee dans l'URL gagne : elle a ete resolue avec le
                // cours complet juste apres la validation, alors que
                // `upcomingLesson` depend d'une progression potentiellement pas
                // encore a jour cote serveur.
                const targetLesson = targetLessonIdFromUrl
                    ? allLessons.find((lesson) => lesson?.id === targetLessonIdFromUrl)
                    : undefined;
                const resumeLesson =
                    targetLesson ?? upcomingLesson ?? storedLesson ?? allLessons[allLessons.length - 1];

                const targetChapter = (courseData?.chapters ?? []).find(
                    (chapter) =>
                        chapter &&
                        chapter.epics &&
                        chapter.epics.some(
                            (epic) =>
                                epic &&
                                epic.lessons &&
                                epic.lessons.some((lesson) => lesson?.id === resumeLesson?.id)
                        )
                );
                const targetEpic = targetChapter?.epics?.find(
                    (epic) =>
                        epic &&
                        epic.lessons &&
                        epic.lessons.some((lesson) => lesson?.id === resumeLesson?.id)
                );

                // Le chapitre annonce dans l'URL sert de filet si la lecon cible
                // n'est pas retrouvee dans l'arbre (filtrage, donnees partielles).
                const urlChapter = targetChapterIdFromUrl
                    ? (courseData?.chapters ?? []).find(
                        (chapter) => chapter?.id === targetChapterIdFromUrl
                    )
                    : undefined;

                if (targetChapter && targetEpic && resumeLesson) {
                    setActiveChapterKey(String(targetChapter.id));
                    setButtonText(
                        `${targetChapter.name} • ${targetEpic.name} • ${resumeLesson.name}`
                    );
                } else if (urlChapter) {
                    setActiveChapterKey(String(urlChapter.id));
                    setButtonText(urlChapter.name);
                } else {
                    // Cours sans lecon exploitable : on retombe sur le premier chapitre.
                    const firstChapter = (courseData?.chapters ?? [])[0];
                    if (firstChapter) {
                        setActiveChapterKey(String(firstChapter.id));
                    }
                }

                // Le bouton "Reprendre" ne pointe une lecon que s'il en reste a faire.
                // La cible explicite prime pour rester coherente avec la section
                // ouverte et la lecon mise en avant.
                if (targetLesson) {
                    setNextLessonId(targetLesson.id);
                } else if (upcomingLesson) {
                    setNextLessonId(upcomingLesson.id);
                }

                const progress =
                    allLessons.length > 0
                        ? (progressionData.length / allLessons.length) * 100
                        : 0;
                setProgressPercent(Math.round(progress));
            })
            .catch((err) => {
                message.error(err.response?.data ?? err.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Renforce l'état payé en interrogeant tc_paiement si besoin
    useEffect(() => {
        let cancelled = false;
        const uid = Number(getUserAppId());
        if (!uid || isTeacher) return;
        if ((getTimeRemaining() ?? -1) > 0) return; // déjà actif via metadata
        (async () => {
            try {
                const { data } = await paiementApi.getStatuses([uid]);
                const statuses: any[] = data?.statuses || [];
                const st = statuses.find((s) => Number(s?.id_userapp) === uid);
                const exp = st?.date_expiration || st?.date_fin;
                if (!cancelled && exp) {
                    const df = new Date(exp);
                    if (!isNaN(df.getTime()) && df.getTime() > Date.now()) {
                        setIsPaid(true);
                    }
                }
            } catch {
                // ignore
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const goToProgress = (lessonId: number) => {
        navigate(`/course-progression/${lessonId}`);
    };

    const handleBackNavigation = () => {
        navigate('/program');
    };

    const summaryStats = useMemo(() => {
        if (!course) {
            return {
                lessons: 0,
                chapters: 0,
                epics: 0,
                completedLessons: 0,
            };
        }
        const lessons = getAllLessonsFromCourse(course);
        const completedLessons = progression.filter((prog) =>
            lessons.some((lesson) => lesson.id === prog.lesson_id)
        ).length;
        const chapters = (course.chapters ?? []).length;
        const epics = (course.chapters ?? []).reduce(
            (acc, chapter) => acc + (chapter?.epics?.length ?? 0),
            0
        );
        return {
            lessons: lessons.length,
            chapters,
            epics,
            completedLessons,
        };
    }, [course, progression]);

    // Lecon sur laquelle l'eleve doit reprendre : celle annoncee par le bouton
    // "Reprendre", sinon la derniere consultee quand le cours est termine.
    const resumeLessonId = useMemo(() => {
        if (targetLessonIdFromUrl > 0) return targetLessonIdFromUrl;
        if (nextLessonId > 0) return nextLessonId;
        const stored = readLastPosition(getUserAppId(), Number(courseId));
        return stored ?? 0;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetLessonIdFromUrl, nextLessonId, courseId]);

    // Apres la validation d'un challenge (?resume=1), on amene l'eleve sur sa
    // position au lieu de le laisser en haut, sur l'introduction du cours.
    useEffect(() => {
        if (hasScrolledToResume.current) return;
        if (!activeChapterKey || !resumeLessonId) return;
        if (searchParams.get('resume') !== '1') return;

        const timer = window.setTimeout(() => {
            const element = document.getElementById(`course-lesson-${resumeLessonId}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                hasScrolledToResume.current = true;
            }
        }, 250);

        return () => window.clearTimeout(timer);
    }, [activeChapterKey, resumeLessonId, searchParams]);

    const collapseItems =
        (course?.chapters || []).map((chapter: TChapter) => {
            const epics = (chapter.epics ?? []).filter((epic) => epic !== null);
            const allLessonsInChapter = epics.flatMap((epic) => epic.lessons ?? []);
            const nextLessonIndex = allLessonsInChapter.findIndex(
                (lesson) => !progression.some((prog) => prog.lesson_id === lesson.id)
            );
            // For free users, calculate next lesson index only considering free lessons
            const freeLessonsInChapter = allLessonsInChapter.filter((lesson) => lesson.is_free === true);
            const nextFreeLessonIndex = freeLessonsInChapter.findIndex(
                (lesson) => !progression.some((prog) => prog.lesson_id === lesson.id)
            );
            // Map the free lesson index back to the full chapter index
            const nextFreeLessonIndexInChapter = nextFreeLessonIndex >= 0 
                ? allLessonsInChapter.findIndex((l) => l.id === freeLessonsInChapter[nextFreeLessonIndex]?.id)
                : -1;
            const progressionInChapter = progression.filter((progress) =>
                allLessonsInChapter.some((lesson) => lesson.id === progress.lesson_id)
            );

            const percentageProgression =
                allLessonsInChapter.length > 0
                    ? (progressionInChapter.length / allLessonsInChapter.length) * 100
                    : 0;

            return {
                key: chapter.id.toString(),
                label: (
                    <div className="course-accordion__header">
                        <span className="course-accordion__title">{chapter.name}</span>
                        <span className="course-accordion__pill">
                            {Math.round(percentageProgression)}%
                        </span>
                    </div>
                ),
                children: (
                    <div className="course-accordion__body">
                        {epics.map((epic) => (
                            <section key={epic.id} className="course-epic">
                                <header className="course-epic__header">
                                    <h3>{epic.name}</h3>
                                    <span>
                                        {(epic.lessons ?? []).length}{' '}
                                        {t('courses.details.lessonsCount', 'lessons')}
                                    </span>
                                </header>
                                <div className="course-lesson-grid">
                                    {(() => {
                                        const lessonsSource = epic.lessons ?? [];
                                        
                                        // Show all lessons but apply sequential + payment gating for non-privileged users
                                        return lessonsSource.map((lesson, idx) => {
                                            const isCompleted = progression.some(
                                                (prog) => prog.lesson_id === lesson.id
                                            );
                                            const lessonIndexInChapter = allLessonsInChapter.findIndex((l) => l.id === lesson.id);
                                            
                                            // Payment gating
                                            const isPaidLesson = lesson.is_free !== true;
                                            const isUnpaidStudent = !isPrivileged && !isPaid;
                                            const showPaymentTooltip = isPaidLesson && isUnpaidStudent;

                                            // Sequential + payment gating
                                            const paymentGateOK = isPrivileged || isPaid || lesson.is_free === true;
                                            const allowedIndexInChapter = (isPrivileged || isPaid)
                                                ? nextLessonIndex
                                                : nextFreeLessonIndexInChapter;
                                            const sequentialGateOK = isCompleted || (allowedIndexInChapter === -1 ? false : lessonIndexInChapter === allowedIndexInChapter);
                                            const isAvailable = isPrivileged || (paymentGateOK && sequentialGateOK);

                                            const statusKey = isCompleted
                                                ? 'completed'
                                                : isAvailable
                                                    ? 'available'
                                                    : 'locked';
                                            
                                            const isResume = lesson.id === resumeLessonId;

                                            const lessonButton = (
                                                <button
                                                    key={lesson.id}
                                                    id={`course-lesson-${lesson.id}`}
                                                    type="button"
                                                    className={`course-lesson ${statusKey}${isResume ? ' is-resume' : ''}`}
                                                    onClick={() => {
                                                        if (isAvailable) {
                                                            goToProgress(lesson.id);
                                                        }
                                                    }}
                                                    disabled={!isAvailable}
                                                >
                                                    <span className="course-lesson__index">
                                                        {idx + 1}
                                                    </span>
                                                    <span className="course-lesson__title">
                                                        {lesson.name}
                                                    </span>
                                                    <span className="course-lesson__status">
                                                        {t(
                                                            `courses.details.lessonStatus.${statusKey}`,
                                                            statusKey
                                                        )}
                                                    </span>
                                                </button>
                                            );
                                            
                                            // Wrap with tooltip if it's a paid lesson for unpaid student
                                            if (showPaymentTooltip) {
                                                return (
                                                    <Tooltip
                                                        key={lesson.id}
                                                        title={t(
                                                            'courses.details.paymentRequired',
                                                            'Cette leçon est réservée aux comptes actifs. Veuillez activer votre abonnement pour y accéder.'
                                                        )}
                                                        placement="top"
                                                    >
                                                        {lessonButton}
                                                    </Tooltip>
                                                );
                                            }
                                            
                                            return lessonButton;
                                        });
                                    })()}
                                </div>
                            </section>
                        ))}
                    </div>
                ),
            };
        }) ?? [];

    return (
        <AppLayout
            title={t('courses.details.pageTitle', 'Course overview')}
            loading={loading}
            onBackNavigation={handleBackNavigation}
            showBackButton={true}
        >
            {!course ? (
                <div className="course-details__empty">
                    {t('courses.details.notFound', 'Course unavailable')}
                </div>
            ) : (
                <div className="course-details-page">
                    <CourseSummaryCard
                        course={course}
                        nextLessonId={nextLessonId}
                        buttonText={buttonText}
                        progressPercent={progressPercent}
                        totalLessons={summaryStats.lessons}
                        completedLessons={summaryStats.completedLessons}
                        chapters={summaryStats.chapters}
                        epics={summaryStats.epics}
                    />

                    <section className="course-accordion">
                        <header className="course-accordion__intro">
                            <span className="course-accordion__eyebrow">
                                {t('courses.details.structureEyebrow', 'Mission roadmap')}
                            </span>
                            <h2>{t('courses.details.structureTitle', 'Chapters & quests')}</h2>
                            <p>
                                {t(
                                    'courses.details.structureSubtitle',
                                    'Progress chapter by chapter and unlock each lesson as you advance.'
                                )}
                            </p>
                        </header>

                        <Collapse
                            accordion
                            activeKey={activeChapterKey ? [activeChapterKey] : undefined}
                            onChange={(keys) =>
                                setActiveChapterKey(
                                    Array.isArray(keys) ? keys[0] : (keys as string | undefined)
                                )
                            }
                            items={collapseItems}
                            className="course-accordion__collapse"
                        />
                    </section>
                </div>
            )}
        </AppLayout>
    );
};

export default CourseDetails;
